import React, { Suspense, lazy } from "react";
import AuthLayout from "../layouts/AuthLayout";
import ShopAuthLayout from "../layouts/ShopAuthLayout";
import DashboardLayout from "../layouts/DashboardLayout";
import ShopRequireAuth from "../pages/Authorization/ShopAuth/ShopRequireAuth";
import CustomerRequireAuth from "../pages/Authorization/CustomerAuth/CustomerRequireAuth";
import PublicLayout from "../layouts/PublicLayout";
import RequireAuth from "../pages/RequireAuth";
import { ROUTE_PRODUCT_PATH } from "../constants/routePath";
import { toolsData } from "../pages/HomePage/Tools/constants/index.js";

// Lazy load components
const HomePage = lazy(() => import("../pages/HomePage"));

// Discover
const Instagram = lazy(() => import("../pages/HomePage/Discover/Instagram"));
const Twitter = lazy(() => import("../pages/HomePage/Discover/Twitter"));
const TikTok = lazy(() => import("../pages/HomePage/Discover/TikTok"));

// Create
const TShirt = lazy(() => import("../pages/HomePage/Create/TShirt"));
const CustomProducts = lazy(() => import("../pages/HomePage/Create/CustomProductsHomePage/index.js"));
const CustomTypeOfCustom = lazy(() => import("../pages/HomePage/Create/CustomCategoryPage/index.js"))
const CustomCollectionPage = lazy(() => import("../pages/HomePage/Create/CustomCollectionPage/index.js"));
const PublicEcommerceProductPage = lazy(() => import("../pages/HomePage/Create/Products/index.js"));
const Designs = lazy(() => import("../pages/HomePage/Designs")); // Import the new Designs page

// Shop
const ShopHomePage = lazy(() => import("../pages/HomePage/Shop/ShopHomePage/index.js"));
const ShopCategoryPage = lazy(() => import("../pages/HomePage/Shop/ShopCategoryPage/index.js"));
const ShopCollectionPage = lazy(() => import("../pages/HomePage/Shop/ShopCollectionPage/index.js"));
const ShopProductPage = lazy(() => import("../pages/HomePage/Shop/ShopProductPage/index.js"));
const ShopWithCart = lazy(() => import("../pages/HomePage/Shop/ShopWithCart/index.js"));

const Tools = lazy(() => import("../pages/HomePage/Tools"));

// Auth
const Auth = lazy(() => import("../pages/Auth"));
const Login = lazy(() => import("../pages/Auth/components/Login"));
const Register = lazy(() => import("../pages/Auth/components/Register"));
const ForgotPassword = lazy(() => import("../pages/Auth/components/ForgotPassword"));
const VerifyEmail = lazy(() => import("../pages/Auth/components/Verification"));
const DigitalDownloadPage = lazy(() => import("../pages/DigitalDownloadPage"));
const ResetPassword = lazy(() => import("../pages/Auth/components/ResetPassword"));

// Email Change Pages
const ConfirmCurrentEmail = lazy(() => import("../pages/EmailChange/ConfirmCurrentEmail"));
const VerifyEmailChange = lazy(() => import("../pages/EmailChange/VerifyEmailChange"));

// Admin Pages
const Dashboard = lazy(() => import("../pages/Dashboard"));
const Income = lazy(() => import("../pages/Income"));
const TransactionDetails = lazy(() => import("../pages/Income/TransactionDetails"));
const Customers = lazy(() => import("../pages/Customers"));
const CustomerDetails = lazy(() => import("../pages/Customers/CustomerDetails"));
const MyStore = lazy(() => import("../pages/MyStore"));
const Products = lazy(() => import("../pages/MyStore/components/Products"));
const Settings = lazy(() => import("../pages/Settings"));
const Store = lazy(() => import("../pages/StorePage"));
const Checkout = lazy(() => import("../pages/UserCheckout"));

// Success Pages
const DigitalProductSuccess = lazy(() => import("../pages/UserCheckout/SuccessPages/DigitalProductSuccess/index.js"));
const CustomProductSuccess = lazy(() => import("../pages/UserCheckout/SuccessPages/CustomProductSuccess/index.js"));
const BookingSuccess = lazy(() => import("../pages/UserCheckout/SuccessPages/BookingProductSuccess/index.js"));
const TipSuccess = lazy(() => import("../pages/UserCheckout/SuccessPages/TipSuccess/index.js"));
const EngagementSuccess = lazy(() => import("../pages/UserCheckout/SuccessPages/EngagementSuccess/index.js"));
const RaffleSuccess = lazy(() => import("../pages/UserCheckout/SuccessPages/RaffleSuccess/index.js"));
const PaypalSuccess = lazy(() => import("../pages/PaypalSuccess")); // Add PayPal success page

// Ecommerce Product Pages
const EcommerceProductPage = lazy(() => import("../pages/StoreComponents/ProductPage"));
const EcomemrceSuccessPage = lazy(() => import("../pages/StoreComponents/ProductPage/Checkout/Success.js"));
const CancelPage = lazy(() => import("../pages/StoreComponents/ProductPage/Checkout/Cancel.js"));

// Product-related lazy imports
const UrlProduct = lazy(() => import("../pages/MyStore/components/Products/Url"));
const DigitalProduct = lazy(() => import("../pages/MyStore/components/Products/Digital"));
const SparkleConnect = lazy(() => import("../pages/MyStore/components/Products/SparkleConnect"));
const CustomProduct = lazy(() => import("../pages/MyStore/components/Products/Custom"));
const EmailProduct = lazy(() => import("../pages/MyStore/components/Products/Email"));
const VideoProduct = lazy(() => import("../pages/MyStore/components/Products/Video"));
const ReferralProduct = lazy(() => import("../pages/MyStore/components/Products/Referral"));
const TipProduct = lazy(() => import("../pages/MyStore/components/Products/TipJar"));
const Engagement = lazy(() => import("../pages/MyStore/components/Products/Engagement"));

// SPOTIFY INTEGRATION
const Spotify = lazy(() => import("../pages/MyStore/components/Products/Spotify"));
const EditSpotify = lazy(() => import("../pages/MyStore/components/Products/Spotify"));

// APPLE MUSIC INTEGRATION
const AppleMusic = lazy(() => import("../pages/MyStore/components/Products/AppleMusic"));
const EditAppleMusic = lazy(() => import("../pages/MyStore/components/Products/AppleMusic"));

// COURSES
const CoursesDashboard = lazy(() => import("../pages/CoursesDashboard"));
const CourseProduct = lazy(() => import("../pages/MyStore/components/Products/Course"));

// RAFFLES
const RaffleSetup = lazy(() => import("../pages/RafflePages/RaffleSetup"));
const RaffleOverview = lazy(() => import("../pages/RafflePages/RaffleOverview"));
const RafflesDashboard = lazy(() => import("../pages/RafflesDashboard"));

// New lazy imports for missing components
const Onboarding = lazy(() => import("../pages/Onboarding"));
const OnboardingSuccess = lazy(() => import("../pages/OnboardingSuccess"));
const Design = lazy(() => import("../pages/MyStore/components/Design"));
const UserProfile = lazy(() => import("../pages/MyStore/components/UserProfile"));
const Billing = lazy(() => import("../pages/Settings/components/Billing"));
const Payments = lazy(() => import("../pages/Settings/components/Payments"));
const Notifications = lazy(() => import("../pages/Settings/components/Notifications"));
const Profile = lazy(() => import("../pages/Settings/components/Profile"));
const Referrals = lazy(() => import("../pages/Referrals"));

// SHOP
const Shop = lazy(() => import("../pages/MyStore/components/Shop"));
const ShopCategory = lazy(() => import("../pages/MyStore/components/Shop/EcommerceShop/index.js"));
const Collections = lazy(() => import("../pages/MyStore/components/Shop/EcommerceShop/Collections"));
const ProductsCollection = lazy(() => import("../pages/MyStore/components/Shop/EcommerceShop/ProductsCollection"));
const ShopEcommerceProductPage = lazy(() => import("../pages/MyStore/components/Shop/EcommerceShop/AddProducts/index.js"));

// Configurator
const ClothingConfigurator = lazy(() => import("../pages/HomePage/Create/ClothingConfigurator"));

// Edit product lazy imports
const EditReferralProduct = lazy(() => import("../pages/MyStore/components/Products/Referral/editReferral"));

const ImageProduct = lazy(() => import("../pages/MyStore/components/Products/Image")); 

// Load ShopDashboard components
const ShopDashboard = lazy(() => import("../pages/ShopDashboard"));
const ShopLogin = lazy(() => import("../pages/Authorization/ShopAuth/Auth/components/Login"));
const ShopCollections = lazy(() => import("../pages/ShopDashboard/Collections"));
const ShopProducts = lazy(() => import("../pages/ShopDashboard/Products"));
const ShopOrders = lazy(() => import("../pages/ShopDashboard/Orders"));
const ShopCustomers = lazy(() => import("../pages/ShopDashboard/Customers/index.js"));
const ShopCustomerDetails = lazy(() => import("../pages/ShopDashboard/Customers/CustomerDetails.js"));
const ShopCustomerOrders = lazy(() => import("../pages/ShopDashboard/Customers/CustomerOrders.js"));
const OrderDetails = lazy(() => import("../pages/ShopDashboard/OrderDetails"));
const ShopViewProducts = lazy(() => import("../pages/ShopDashboard/ProductList/index.js"));
const ReadyMadeProducts = lazy(() => import("../pages/ShopDashboard/ReadyMadeProducts/index.js"));
const GoogleMerchant = lazy(() => import("../pages/ShopDashboard/GoogleMerchant/index.js"));
const GoogleMerchantCallback = lazy(() => import("../pages/ShopDashboard/GoogleMerchant/Callback.js"));
const FacebookCatalog = lazy(() => import("../pages/ShopDashboard/FacebookCatalog/index.js"));
const TikTokCatalog = lazy(() => import("../pages/ShopDashboard/TikTokCatalog/index.js"));
const ShopDesigns = lazy(() => import("../pages/ShopDashboard/Designs/index.js"));
const ShopDesignCategories = lazy(() => import("../pages/ShopDashboard/Designs/Categories/index.js"));
const Creators = lazy(() => import("../pages/ShopDashboard/Creators/index.js"));
const CreatorDetails = lazy(() => import("../pages/ShopDashboard/CreatorDetails/index.js"));

// Legal Pages
const PrivacyPolicy = lazy(() => import("../pages/HomePage/Legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("../pages/HomePage/Legal/TermsOfService"));
const LegalHub = lazy(() => import('../pages/HomePage/Legal/index.js'));
const CookiePolicy = lazy(() => import("../pages/HomePage/Legal/CookiePolicy"));
const AcceptableUsePolicy = lazy(() => import("../pages/HomePage/Legal/AUPolicy"));
const RefundPolicy = lazy(() => import("../pages/HomePage/Legal/RefundPolicy"));
const DataDeletionPolicy = lazy(() => import("../pages/HomePage/Legal/DataDeletionPolicy"));
const CopyrightInfringement = lazy(() => import("../pages/HomePage/Legal/CopyrightInfringement"));

// Video Call Page
const VideoCallPage = lazy(() => import("../pages/Communication/VideoCallPage"));
const PublicCallPage = lazy(() => import("../pages/Communication/PublicCallPage"));
const MeetingLobby = lazy(() => import("../pages/Communication/MeetingLobby/index.js"));
const WaitingPage = lazy(() => import("../pages/Communication/PublicCallPage/WaitingPage.js"));

// Customer-specific pages
const CustomerLogin = lazy(() => import("../pages/Customer/Login"));
const CustomerSignup = lazy(() => import("../pages/Customer/Signup"));
const CustomerPricing = lazy(() => import("../pages/Customer/Pricing"));
const CustomerAccount = lazy(() => import("../pages/Customer/Account"));
const CourseViewer = lazy(() => import("../pages/Customer/CourseViewer"));

//404
const NotFound = lazy(() => import("../pages/NotFound"));

const currentDomain = window.location.hostname;

export const ROUTES = (() => {
  switch (currentDomain) {
    case 'admin.sparkle.store':
    case 'localhost':
      return [
        {
          path: '/',
          element: <AuthLayout />,
          children: [
            { index: true, element: <Suspense fallback={null}><Login /></Suspense> },
            { path: 'auth/login', element: <Suspense fallback={null}><Login /></Suspense> },
            { path: 'auth/register', element: <Suspense fallback={null}><Register /></Suspense> },
            { path: 'auth/forgot', element: <Suspense fallback={null}><ForgotPassword /></Suspense> },
            { path: 'auth/reset-password', element: <Suspense fallback={null}><ResetPassword /></Suspense> },
            { path: 'auth', element: <Suspense fallback={null}><Auth /></Suspense> },
          ],
        },
        {
          path: '/',
          element: <RequireAuth />,
          children: [
            {
              path: '/',
              element: <DashboardLayout />,
              children: [
                { path: 'dashboard', element: <Suspense fallback={null}><Dashboard /></Suspense> },
                { path: 'income', element: <Suspense fallback={null}><Income /></Suspense> },
                { path: 'income/:transactionId', element: <Suspense fallback={null}><TransactionDetails /></Suspense> },
                { path: 'subscribers', element: <Suspense fallback={null}><Customers /></Suspense> },
                { path: 'billing', element: <Suspense fallback={null}><Billing /></Suspense> },
                { path: 'subscriber/:customerId', element: <Suspense fallback={null}><CustomerDetails /></Suspense> },
                { path: 'store', element: <Suspense fallback={null}><MyStore /></Suspense> },
                { path: 'products', element: <Suspense fallback={null}><Products /></Suspense> },
                { path: 'settings', element: <Suspense fallback={null}><Settings /></Suspense> },
                { path: ROUTE_PRODUCT_PATH.URL_PRODUCT, element: <Suspense fallback={null}><UrlProduct /></Suspense> },

                { path: ROUTE_PRODUCT_PATH.DIGITAL_PRODUCT, element: <Suspense fallback={null}><DigitalProduct /></Suspense> },
                { path: ROUTE_PRODUCT_PATH.EDIT_DIGITAL_PRODUCT, element: <Suspense fallback={null}><DigitalProduct /></Suspense> },

                { path: ROUTE_PRODUCT_PATH.TIP_PRODUCT, element: <Suspense fallback={null}><TipProduct /></Suspense> },
                { path: ROUTE_PRODUCT_PATH.EDIT_TIP_PRODUCT, element: <Suspense fallback={null}><TipProduct /></Suspense> },

                { path: ROUTE_PRODUCT_PATH.ENGAGEMENT, element: <Suspense fallback={null}><Engagement /></Suspense> },
                { path: ROUTE_PRODUCT_PATH.EDIT_ENGAGEMENT, element: <Suspense fallback={null}><Engagement /></Suspense> },

                { path: ROUTE_PRODUCT_PATH.SPARKLE_CONNECT, element: <Suspense fallback={null}><SparkleConnect /></Suspense> },
                { path: ROUTE_PRODUCT_PATH.SPARKLE_CONNECT_EDIT, element: <Suspense fallback={null}><SparkleConnect /></Suspense> },

                { path: ROUTE_PRODUCT_PATH.CUSTOM_PRODUCT, element: <Suspense fallback={null}><CustomProduct /></Suspense> },
                { path: ROUTE_PRODUCT_PATH.EDIT_CUSTOM_PRODUCT, element: <Suspense fallback={null}><CustomProduct /></Suspense> },

            { path: ROUTE_PRODUCT_PATH.EMAIL_PRODUCT, element: <Suspense fallback={null}><EmailProduct /></Suspense> },

            { path: ROUTE_PRODUCT_PATH.SPOTIFY, element: <Suspense fallback={null}><Spotify /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.EDIT_SPOTIFY_PRODUCT, element: <Suspense fallback={null}><EditSpotify /></Suspense> },

            {
              path: ROUTE_PRODUCT_PATH.APPLE_MUSIC,
              element: <Suspense fallback={null}><AppleMusic /></Suspense>
            },
            {
              path: ROUTE_PRODUCT_PATH.EDIT_APPLE_MUSIC_PRODUCT,
              element: <Suspense fallback={null}><EditAppleMusic /></Suspense>
            },

            // COURSES
            { path: ROUTE_PRODUCT_PATH.COURSE_DASHBOARD, element: <Suspense fallback={null}><CoursesDashboard /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.COURSE_PRODUCT, element: <Suspense fallback={null}><CourseProduct /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.EDIT_COURSE_PRODUCT, element: <Suspense fallback={null}><CourseProduct /></Suspense> },

            // RAFFLES
            { path: ROUTE_PRODUCT_PATH.RAFFLE_DASHBOARD, element: <Suspense fallback={null}><RafflesDashboard /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.RAFFLE_SETUP, element: <Suspense fallback={null}><RaffleSetup /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.RAFFLE_OVERVIEW, element: <Suspense fallback={null}><RaffleOverview /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.EDIT_RAFFLE, element: <Suspense fallback={null}><RaffleSetup /></Suspense> },

            { path: ROUTE_PRODUCT_PATH.VIDEO_PRODUCT, element: <Suspense fallback={null}><VideoProduct /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.REFERRAL_PRODUCT, element: <Suspense fallback={null}><ReferralProduct /></Suspense> },
            { path: 'onboarding', element: <Suspense fallback={null}><Onboarding /></Suspense> },
            { path: 'onboarding-success', element: <Suspense fallback={null}><OnboardingSuccess /></Suspense> },
            { path: 'design', element: <Suspense fallback={null}><Design /></Suspense> },
            { path: 'profile', element: <Suspense fallback={null}><UserProfile /></Suspense> },
            { path: 'settings/billing', element: <Suspense fallback={null}><Billing /></Suspense> },
            { path: 'settings/profile', element: <Suspense fallback={null}><Profile /></Suspense> },
            { path: 'settings/payments', element: <Suspense fallback={null}><Payments /></Suspense> },
            { path: 'settings/notifications', element: <Suspense fallback={null}><Notifications /></Suspense> },
            { path: 'referrals', element: <Suspense fallback={null}><Referrals /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.EDIT_URL_PRODUCT, element: <Suspense fallback={null}><UrlProduct /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.IMAGE_PRODUCT, element: <Suspense fallback={null}><ImageProduct /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.EDIT_EMAIL_PRODUCT, element: <Suspense fallback={null}><EmailProduct /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.EDIT_VIDEO_PRODUCT, element: <Suspense fallback={null}><VideoProduct /></Suspense> },
            { path: ROUTE_PRODUCT_PATH.EDIT_REFERRAL_PRODUCT, element: <Suspense fallback={null}><EditReferralProduct /></Suspense> },

            // 3D CONFIGURATOR
            { path: 'create/product/customize', element: <Suspense fallback={null}><ClothingConfigurator /></Suspense> },            // CREATOR STORE (formerly shop)
            { path: 'creator-store', element: <Suspense fallback={null}><Shop /></Suspense> },
            { path: 'creator-store/home', element: <Suspense fallback={null}><ShopCategory /></Suspense> },
            { path: 'creator-store/:category', element: <Suspense fallback={null}><Collections /></Suspense> },
            { path: 'creator-store/:category/:collection', element: <Suspense fallback={null}><ProductsCollection /></Suspense> },
            { path: 'creator-store/:category/:collection/:productSlug', element: <Suspense fallback={null}><ShopEcommerceProductPage /></Suspense> },

            // VIDEO CALL PAGE
            { path: 'video-call', element: <Suspense fallback={null}><VideoCallPage /></Suspense> },
            
            // TESTING VIDEO CALL PAGE
            {
              path: 'sparkle-call',
              element: <Suspense fallback={null}><PublicCallPage /></Suspense>
            },
            {
              path: 'waiting-room',
              element: <Suspense fallback={null}><WaitingPage /></Suspense>,
            },
              ]
            }
          ],
        },
      ];
    case 'shop.sparkle.store':
      return [
        {
          path: '/',
          element: <ShopAuthLayout />,
          children: [
            { index: true, element: <Suspense fallback={null}><ShopLogin /></Suspense> },
            { path: 'auth/login', element: <Suspense fallback={null}><ShopLogin /></Suspense> },
          ],
        },
        {
          path: '/',
          element: <ShopRequireAuth />,
          children: [
            { path: 'store/dashboard', element: <Suspense fallback={null}><ShopDashboard /></Suspense> },
            { path: 'store/collections', element: <Suspense fallback={null}><ShopCollections /></Suspense> },
            { path: 'store/products/:id?', element: <Suspense fallback={null}><ShopProducts /></Suspense> },            { path: 'view/products', element: <Suspense fallback={null}><ShopViewProducts /></Suspense> },
            { path: 'store/ready-made-products', element: <Suspense fallback={null}><ReadyMadeProducts /></Suspense> },
            { path: 'view/orders', element: <Suspense fallback={null}><ShopOrders /></Suspense> },
            { path: 'view/customers', element: <Suspense fallback={null}><ShopCustomers /></Suspense> },
            { path: 'shop/customers/:id', element: <Suspense fallback={null}><ShopCustomerDetails /></Suspense> },
            { path: 'shop/customers/:id/orders', element: <Suspense fallback={null}><ShopCustomerOrders /></Suspense> },
            { path: 'view/orders/:id', element: <Suspense fallback={null}><OrderDetails /></Suspense> },
            { path: 'store/creators', element: <Suspense fallback={null}><Creators /></Suspense> },
            { path: 'store/creators/:id', element: <Suspense fallback={null}><CreatorDetails /></Suspense> },
            { path: 'store/google-merchant', element: <Suspense fallback={null}><GoogleMerchant /></Suspense> },
            { path: 'store/facebook-catalog', element: <Suspense fallback={null}><FacebookCatalog /></Suspense> },
            { path: 'store/tiktok-catalog', element: <Suspense fallback={null}><TikTokCatalog /></Suspense> },
            { path: 'store/designs', element: <Suspense fallback={null}><ShopDesigns /></Suspense> },
            { path: 'store/design-categories', element: <Suspense fallback={null}><ShopDesignCategories /></Suspense> },
          ],
        },
        // Add the Google Merchant OAuth callback route outside of authentication
        {
          path: '/auth/google/callback',
          element: <Suspense fallback={null}><GoogleMerchantCallback /></Suspense>
        },
      ];
    default:
      return [
        {
          path: '/',
          element: <PublicLayout />,
          children: [
            { index: true, element: <Suspense fallback={null}><HomePage /></Suspense> },
            
            // Public customer routes
            { path: 'customer/login', element: <Suspense fallback={null}><CustomerLogin /></Suspense> },
            { path: 'customer/signup', element: <Suspense fallback={null}><CustomerSignup /></Suspense> },
            { path: 'customer/pricing', element: <Suspense fallback={null}><CustomerPricing /></Suspense> },
            
            { path: 'tools', element: <Suspense fallback={null}><Tools /></Suspense> },
            ...toolsData.map(tool => ({
              path: `tools/${tool.link.split('/').pop()}`,
              element: <Suspense fallback={null}>{React.createElement(lazy(() => import(`../pages/HomePage/Tools/pages/${tool.file.replace(/\s+/g, '')}`)))}</Suspense>,
            })),
            { path: 'creator/link-in-bio-for-instagram', element: <Suspense fallback={null}><Instagram /></Suspense> },
            { path: 'creator/link-in-bio-for-twitter', element: <Suspense fallback={null}><Twitter /></Suspense> },
            { path: 'creator/link-in-bio-for-tiktok', element: <Suspense fallback={null}><TikTok /></Suspense> },
            { path: 'designs', element: <Suspense fallback={null}><Designs /></Suspense> },

            { path: ':storeName', element: <Suspense fallback={null}><Store /></Suspense> },
            { path: ':storeName/checkout/:productSlug', element: <Suspense fallback={null}><Checkout /></Suspense> },
            // Digital product download/success page
            {
              path: ':storeName/checkout/success',
              element: <Suspense fallback={null}><DigitalProductSuccess /></Suspense>
            },
            // Custom Product Success
            {
              path: 'fulfillment/:token',
              element: <Suspense fallback={null}><CustomProductSuccess /></Suspense>
            },
            // Booking Product Success
            {
              path: ':storeName/checkout/success-booking',
              element: <Suspense fallback={null}><BookingSuccess /></Suspense>
            },
            {
              path: ':storeName/checkout/success-tip',
              element: <Suspense fallback={null}><TipSuccess /></Suspense>
            },
            {
              path: ':storeName/checkout/success/:transactionId',
              element: <Suspense fallback={null}><DigitalProductSuccess /></Suspense>
            },
            {
              path: ':storeName/checkout/success-engagement',
              element: <Suspense fallback={null}><EngagementSuccess /></Suspense>
            },
            {
              path: ':storeName/checkout/success-raffle',
              element: <Suspense fallback={null}><RaffleSuccess /></Suspense>
            },
            {
              path: ':storeName/checkout/success-paypal',
              element: <Suspense fallback={null}><PaypalSuccess /></Suspense>
            },

            // Ecommerce Success and Cancel
            { path: 'checkout/success', element: <Suspense fallback={null}><EcomemrceSuccessPage /></Suspense> },
            { path: 'checkout/cancel', element: <Suspense fallback={null}><CancelPage /></Suspense> },

            { path: ':storeName/product/:productSlug', element: <Suspense fallback={null}><EcommerceProductPage /></Suspense> },
            { path: '*', element: <Suspense fallback={null}><NotFound /></Suspense> },
            {
              path: 'verify-email',
              element: <Suspense fallback={null}><VerifyEmail /></Suspense>
            },
            {
              path: 'confirm-current-email',
              element: <Suspense fallback={null}><ConfirmCurrentEmail /></Suspense>
            },
            {
              path: 'verify-email-change',
              element: <Suspense fallback={null}><VerifyEmailChange /></Suspense>
            },
            {
              path: '/digital-download/:token',
              element: <Suspense fallback={null}><DigitalDownloadPage /></Suspense>
            },

            // VIDEO CALL PAGE
            {
              path: 'sparkle-call',
              element: <Suspense fallback={null}><PublicCallPage /></Suspense>
            },
            {
              path: 'meeting-lobby',
              element: <Suspense fallback={null}><MeetingLobby /></Suspense>,
            },
            {
              path: 'waiting-room',
              element: <Suspense fallback={null}><WaitingPage /></Suspense>,
            },

            // Legal Pages
            {
              path: 'legal/privacy-policy',
              element: <Suspense fallback={null}><PrivacyPolicy /></Suspense>
            },
            {
              path: 'legal/terms-of-service',
              element: <Suspense fallback={null}><TermsOfService /></Suspense>
            },
            {
              path: '/legal',
              element: <LegalHub />,
            },
            {
              path: 'legal/cookie-policy',
              element: <Suspense fallback={null}><CookiePolicy /></Suspense>
            },
            {
              path: 'legal/acceptable-use-policy',
              element: <Suspense fallback={null}><AcceptableUsePolicy /></Suspense>
            },
            {
              path: 'legal/copyright-infringement-dmca',
              element: <Suspense fallback={null}><CopyrightInfringement /></Suspense>
            },
            {
              path: 'legal/refund-policy',
              element: <Suspense fallback={null}><RefundPolicy /></Suspense>
            },
            {
              path: 'legal/data-deletion-policy',
              element: <Suspense fallback={null}><DataDeletionPolicy /></Suspense>
            },

            // Create Custom Pages            
            { path: 'create/product/customize', element: <Suspense fallback={null}><ClothingConfigurator /></Suspense> },
            { path: 'create/t-shirts', element: <Suspense fallback={null}><TShirt /></Suspense> },

            { path: 'custom-products', element: <Suspense fallback={null}><CustomProducts /></Suspense> },            { path: 'custom/:category', element: <Suspense fallback={null}><CustomTypeOfCustom /></Suspense> },
            { path: 'custom/:category/:collectionSlug', element: <Suspense fallback={null}><CustomCollectionPage /></Suspense> },
            { path: 'custom/:category/:collectionSlug/:productSlug', element: <Suspense fallback={null}><PublicEcommerceProductPage /></Suspense> },            // Shop Pages - Ready-made designs (wrapped with cart context)
            {
              path: 'shop',
              element: <Suspense fallback={null}><ShopWithCart /></Suspense>,
              children: [
                { index: true, element: <Suspense fallback={null}><ShopHomePage /></Suspense> },
                { path: ':category', element: <Suspense fallback={null}><ShopCategoryPage /></Suspense> },
                { path: ':category/product/:productSlug', element: <Suspense fallback={null}><ShopProductPage /></Suspense> }, // Direct/tagged products - must come first
                { path: ':category/:collectionSlug/:productSlug', element: <Suspense fallback={null}><ShopProductPage /></Suspense> },
                { path: ':category/:collectionSlug', element: <Suspense fallback={null}><ShopCollectionPage /></Suspense> },
                { path: ':category/:productSlug', element: <Suspense fallback={null}><ShopProductPage /></Suspense> }, // Legacy direct products route
              ],
            },
          ],
        },
        // Add protected customer routes with CustomerRequireAuth
        {
          path: '/',
          element: <CustomerRequireAuth />,
          children: [
            // Protected customer routes
            { path: 'customer/account', element: <Suspense fallback={null}><CustomerAccount /></Suspense> },
            { path: 'course/:courseId', element: <Suspense fallback={null}><CourseViewer /></Suspense> },
          ],
        },
      ];
  }
})();
